import * as React from "react"
import { useState, useEffect, FunctionComponent } from "react"
import { useNavigate, useParams } from "react-router-dom"
import Button from "@murphy-frontend/web-core/components/Button"
import Spinner from "@murphy-frontend/web-core/components/Spinner"
import { dashboardTypes } from "@murphy-frontend/web-core/constants"
import { translateConstants } from "../../../common/services/translationService"
import { useGetDashboard, useGetDashboardMembers } from "../../../common/api/Dashboards/queries"
import {
	useCreateDashboard,
	useEditDashboard} from "../../../common/api/Dashboards/mutations"
import { useModal } from "@murphy-frontend/web-core/contexts/ModalContext"
import { useUserPreferences } from "../../../common/contexts/UserPreferencesContext"
import { ModalConfiguration } from "@murphy-frontend/web-core/components/Modal"
import { useCustomer } from "../../../common/contexts/CustomerContext"
import { useUser } from "../../../common/contexts/UserContext"
import DeleteTemplate from "../components/DeleteTemplate"
import ComponentWrapper from "../../../common/components/ComponentWrapper"
import TabPanel from "@murphy-frontend/web-core/components/Tabs/TabPanel"
import NewCrisisInfoTab from "./NewCrisisInfoTab"
import { MdArrowBackIosNew } from "react-icons/md"
import NewCrisisAccessTab from "./NewCrisisAccessTab"
import NewCrisisConfirmTab from "./NewCrisisConfirmTab"
import { IconContext } from "react-icons/lib"
import { IoChevronBackOutline } from "react-icons/io5"
import useWindowDimensions from "@murphy-frontend/web-core/hooks/useWindowDimensions"

const styles = {
	backContainer: {
		borderWidth: 1,
		borderStyle: 'solid',
		borderColor: '#EBEBEB',
		backgroundColor: 'white',
		justifyContent: 'space-between',
		width: '5rem',
		marginBottom: 20,
		padding: 10,
		display: 'flex',
		alignItems: 'center',
		cursor: 'pointer'
	},
}
const UpsertDashboard: FunctionComponent = () => {
	const {currentSize, width} = useWindowDimensions();
	const { id } = useParams();
	const [dashboardId, setDashboardId] = useState(id);
	const navigate = useNavigate()
	const noSelection = "No selection"
	const { customer } = useCustomer()
	const { user } = useUser()

	const { openModal, closeModal } = useModal()
	const { translations, language } = useUserPreferences()

	const [error, setError] = useState(null)
	const [isLoading, setIsLoading] = useState(false)
	const [isEditMode, setIsEditMode] = useState(false)

	const [isArchived, setIsArchived] = useState(null)
	const [dashboardName, setDashboardName] = useState("")
	const [oldDashboardName, setOldDashboardName] = useState("")

	const [dashboardMembers, setDashboardMembers] = useState([])

	const [selectedContacts, setSelectedContacts] = useState([]);

	const [selectedTemplateId, setSelectedTemplateId] = useState(noSelection);
	const [selectedTemplateName, setSelectedTemplateName] = useState();

	const [allDashboardTypes, setAllDashboardTypes] = useState([])
	const [selectedDashboardTypeId, setSelectedDashboardTypeId] = useState(1);
	const [selectedDashboardTypeName, setSelectedDashboardTypeName] = useState('Open');
	const [oldSelectedDashboardTypeId, setOldSelectedDashboardTypeId] = useState(1)

	const [dashboardNameErrorMessage, setDashboardNameErrorMessage] = useState("")
	const [dashboardTypeErrorMessage, setDashboardTypeErrorMessage] = useState("");
	const [selectedTabId, setSelectedTabId] = useState<number>(1);



	const {
		isLoading: isLoadingDasboard,
		isError: isErrorDashboard,
		data: dataDashboard,
		error: errorDashboard
	} = useGetDashboard(id)

	const {
		isLoading: isLoadingDasboardMembers,
		isError: isErrorDashboardMembers,
		data: dataDashboardMembers,
		error: errorDashboardMembers
	} = useGetDashboardMembers(id, selectedDashboardTypeId)

	const { mutate: editDashboard, isPending: editDashboardIsLoading } = useEditDashboard(customer.Id, translations)
	const { mutate: createDashboard, isPending: createDashboardIsLoading, data: newDashboardData } = useCreateDashboard(customer.Id, translations)

	const mapDashboard = rawDashboard => ({
		id: rawDashboard.dashboardid,
		name: decodeURIComponent(rawDashboard.Name),
		isarchived: rawDashboard.Arkiverad,
		dashboardtypeid: rawDashboard.DashboardTypeId
	})

	const mapDashboardMembers = members =>
		members.map(memb => ({
			id: memb.Id,
			name: memb.Name,
			customeruserid: memb.CustomerUserId.toUpperCase(),
			type: "user"
		}))

	useEffect(() => {
		if (dataDashboard) {
			const mappedDashboard = mapDashboard(dataDashboard)
			setDashboardName(mappedDashboard.name)
			setOldDashboardName(mappedDashboard.name)
			setIsArchived(mappedDashboard.isarchived)
			setSelectedDashboardTypeId(mappedDashboard.dashboardtypeid);
			setSelectedDashboardTypeName(allDashboardTypes.filter(dash => dash.id === mappedDashboard.dashboardtypeid)[0]?.value);
			setOldSelectedDashboardTypeId(mappedDashboard.dashboardtypeid)
		}
	}, [dataDashboard])

	useEffect(() => {
		if (dataDashboardMembers) {
			const membersWithPermission = dataDashboardMembers.filter(memb => memb.hasPermission === true)
			const mappedMembers = mapDashboardMembers(membersWithPermission)
			setDashboardMembers(mappedMembers)
		}
	}, [dataDashboardMembers])

	const onClickDeleteTemplate = () => {
		const modalConf: ModalConfiguration = {
			title: translations.deletetemplate,
			body: (
				<DeleteTemplate
					customerId={customer.Id}
					templateId={selectedTemplateId}
					closeModal={closeModal}
					translations={translations}
				/>
			),
			hideButton: true,
			disableOverflow: true,
			type: "info"
		}

		openModal(modalConf)
	}

	useEffect(() => {
		if (dashboardId) {
			setIsEditMode(true)
		} else {
			setDashboardName("")
			setIsEditMode(false)
		}
	}, [dashboardId])

	useEffect(() => {
		setAllDashboardTypes(translateConstants(language, dashboardTypes))
	}, [])

	useEffect(() => {
		setAllDashboardTypes(translateConstants(language, dashboardTypes))
	}, [language])


	function ErrorMessage() {
		return <p>{error}</p>
	}

	const onSubmitArchiveDashboard = () => {
		const editDashboardRequest = {
			DashboardId: id,
			ArchiveBoard: true
		}

		editDashboard(editDashboardRequest, {
			onSuccess: () => {
				navigate("/crisis/")
			}
		})
	}

	const onSubmitActivateDashboard = () => {
		const editDashboardRequest = {
			DashboardId: id,
			ActivateBoard: true
		}

		editDashboard(editDashboardRequest, {
			onSuccess: () => {
				navigate("/crisis/")
			}
		})
	}

	function ArchiveConfirmationBody() {
		return (
			<p>
				{translations.archive} <strong>{dashboardName}</strong> ?
			</p>
		)
	}

	function ActivateConfirmationBody() {
		return (
			<p>
				{translations.activate} <strong>{dashboardName}</strong> ?
			</p>
		)
	}

	const onClickArchiveDashboard = () => {
		const modalConf: ModalConfiguration = {
			title: `${translations.archive} dashboard`,
			body: <ArchiveConfirmationBody />,
			hideButton: false,
			buttonText: translations.archive,
			type: "warning",

			okCallback: () => onSubmitArchiveDashboard()
		}

		openModal(modalConf)
	}

	const onClickActivateDashboard = () => {
		const modalConf: ModalConfiguration = {
			title: `${translations.activate} dashboard`,
			body: <ActivateConfirmationBody />,
			hideButton: false,
			buttonText: translations.activate,
			type: "info",

			okCallback: () => onSubmitActivateDashboard()
		}
		openModal(modalConf)
	}

	const onClickNewDashboard = () => {
		navigate("/dashboards/")
	}

	const onClickBack = () => {
		navigate("/crisis")
	}

	useEffect(() => {
		if (oldSelectedDashboardTypeId !== selectedDashboardTypeId) {
			const updateDashboardRequest = {
				DashboardId: dashboardId
			}

			if (dashboardName) {
				updateDashboardRequest.NewName = encodeURIComponent(dashboardName)
			}

			if (selectedDashboardTypeId > -1) {
				updateDashboardRequest.Dashboardtype = selectedDashboardTypeId
			}

			editDashboard(updateDashboardRequest, {
				onSuccess: () => {
					// navigate('/crisis')
					setOldSelectedDashboardTypeId(selectedDashboardTypeId);
				}
			})
		}
	}, [selectedDashboardTypeId])

	const onClickSave = () => {
		if (isEditMode) {
			const updateDashboardRequest = {
				DashboardId: dashboardId
			}

			if (dashboardName) {
				updateDashboardRequest.NewName = encodeURIComponent(dashboardName)
			}

			if (selectedDashboardTypeId > -1) {
				updateDashboardRequest.Dashboardtype = selectedDashboardTypeId
			}

			editDashboard(updateDashboardRequest, {
				onSuccess: () => {
					navigate('/crisis')
				}
			})
		} else {
			if (!dashboardName) {
				setDashboardNameErrorMessage("Must set name")
				return
			}

			if (selectedDashboardTypeId < 0) {
				setDashboardTypeErrorMessage("Must select type")
				return
			}

			const createDashboardRequest = {
				Name: encodeURIComponent(dashboardName),
				Dashboardtype: selectedDashboardTypeId,
				CustomerId: customer.Id
			}

			if (selectedTemplateId && selectedTemplateId !== noSelection) {
				createDashboardRequest.TemplateId = selectedTemplateId
			}

			createDashboard(createDashboardRequest, {
				onSuccess: (res) => {
					// navigate('/crisis')
					if (res?.data) {
						setDashboardId(res.data.dashboardid)
						setSelectedTabId(selectedTabId + 1);
					}
				}
			});
		}
	}

	useEffect(() => {
		if (error) {
			const modalConf: ModalConfiguration = {
				title: "Error",
				body: <ErrorMessage />,
				buttonText: "Ok",
				type: "error",
				okCallback: () => {
					setError(null)
				}
			}

			openModal(modalConf)
		}
	}, [error])

	if (
		isLoading ||
		isLoadingDasboard ||
		isLoadingDasboardMembers ||
		editDashboardIsLoading ||
		createDashboardIsLoading
	) {
		return <Spinner isGlobal />
	}

	const saveButtonDisabled = !!dashboardNameErrorMessage

	const onClickTab = (tabId: number) => {
		setSelectedTabId(tabId);
	};


	const onNext = () => {
		if (selectedTabId === 3) {
			onClickSave();
		} else {
			setSelectedTabId(selectedTabId + 1);
		}

	};

	const moveToTab = (tabId) => {
		setSelectedTabId(tabId);
	}


	const onBack = () => {
		setSelectedTabId(selectedTabId - 1);
	}
	const tabs = [
		{
			id: 1,
			displayText: `1. ${translations.information || "Information"}`,
			customClass: "tab-transparent-panel-tab",
			customSelectedClass: "tab-transparent-panel-tab-selected wrap-text"
		},
		{
			id: 2,
			displayText: `2. ${translations.access || "Access"}`,
			customClass: "tab-transparent-panel-tab",
			customSelectedClass: "tab-transparent-panel-tab-selected wrap-text"
		},
		{
			id: 3,
			displayText: `3. ${translations.confirm || "Confirm"}`,
			customClass: "tab-transparent-panel-tab",
			customSelectedClass: "tab-transparent-panel-tab-selected wrap-text"
		},
	]

	const renderTabBody = () => {
		switch (selectedTabId) {
			case 1:
				return (
					<NewCrisisInfoTab
						dashboardName={dashboardName}
						setDashboardName={(name) => setDashboardName(name)}
						selectedTemplateId={selectedTemplateId}
						setSelectedTemplateId={(tempId) => setSelectedTemplateId(tempId)}
						setSelectedTemplateName={(tempName) => setSelectedTemplateName(tempName)}
						isEditMode={isEditMode}
						currentScreenSize={width}
					/>
				);
			case 2:
				return (
					<NewCrisisAccessTab
					    id = {dashboardId}
						selectedDashboardTypeId={selectedDashboardTypeId}
						setSelectedDashboardTypeName={(name) => setSelectedDashboardTypeName(name)}
						setSelectedDashboardTypeId={(id) => setSelectedDashboardTypeId(id)}
						isEditMode={isEditMode}
						onSelectContacts={(contacts) => setSelectedContacts(contacts)}
							currentScreenSize={width}
					/>
				);
			case 3:
				return (
					<NewCrisisConfirmTab
						dashboardName={dashboardName}
						templateName={selectedTemplateName}
						dashboardType={selectedDashboardTypeName}
						dashboardTypeId={selectedDashboardTypeId}
						selectedContacts={selectedContacts}
						isEditMode={isEditMode}
						moveToTab={moveToTab}
						currentScreenSize={width}
					/>
				);
		}
	};
  const isMobile = width <= 768;
  const sidebarWidth = isMobile ? 20 : 40;
  const marginLeft = isMobile ? 0 : 20;
  console.log(isMobile);
	return (
		<ComponentWrapper active={5}>
      <div style={{ width: '100%', maxWidth: isMobile ? '100%' : `calc(100% - ${sidebarWidth}px)`, marginLeft: `${marginLeft}px`, padding: isMobile ? '10px' : '20px' }}>
        <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'flex-start' : 'center', gap: isMobile ? '10px' : '20px', marginBottom: '20px' }}>
          <div className="header-back-btn">
            <Button buttonSize="btn--medium" isLightButton={true} onClick={() => navigate(-1)}>
              <IconContext.Provider value={{ className: 'btn-icon' }}>
                <IoChevronBackOutline />
              </IconContext.Provider>
              &nbsp; {translations.back}
            </Button>
          </div>
          <h1 style={{ alignSelf: 'center', width: isMobile ? '100%' : '80%', fontSize: isMobile ? '18px' : '24px' }}>
            {translations.createOperation || 'Create a new operation'}
          </h1>
        </div>

        <div style={{ width: '100%', maxWidth: isMobile ? '100%' : '80rem', backgroundColor: '#E0E1DD', height: '5px' }}>
          <div
            style={{
              backgroundColor: '#00A098',
              width: isMobile ? `${(selectedTabId / tabs.length) * 100}%` : `${(selectedTabId / tabs.length) * 80}rem`,
              height: '5px',
            }}
          />
        </div>
        <div className="container" style={{ padding: isMobile ? '10px' : '20px' }}>
          <TabPanel
            tabs={tabs}
            onClickTab={onClickTab}
            clickable={false}
            selectedTabId={selectedTabId}
            customClass="tab-solid-panel-tms"
          />
          <div style={{ minHeight: isMobile ? 'auto' : '50rem' }}>
            <div style={{ minHeight: isMobile ? 'auto' : '30rem' }}>
              {renderTabBody()}
            </div>
            <div
              style={{
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                marginTop: isMobile ? '20px' : '60px',
                paddingLeft: isMobile ? '0' : '20px',
                justifyContent: isMobile ? 'flex-start' : 'center',
                gap: isMobile ? '10px' : '20px',
              }}
            >
              {selectedTabId > 1 && (
				              <div style={{ marginLeft: isMobile ? '0' : '20px' }}>

                <Button buttonStyle="btn--primary" buttonSize="btn--medium" onClick={onBack}>
                  {translations.back || 'Back'}
                </Button> </div>
              )}
              <div style={{ marginLeft: isMobile ? '0' : '20px' }}>
                <Button
                  buttonStyle="btn--primary"
                  buttonSize="btn--medium"
                  onClick={selectedTabId === 1 && !isEditMode ? onClickSave : onNext}
                  disabled={selectedTabId === 1 && (!dashboardName || dashboardName === '')}
                >
                  {selectedTabId === tabs.length ? translations.eamodalbtnsave || 'Create' : translations.nextBox || 'Next'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </ComponentWrapper>
	)
}

export default UpsertDashboard
